import React, { useEffect, useMemo, useRef, useState } from "react";

import styles from "./CommentsPanel.module.scss";

import { getRecentVideoComments ,
         RecentVideoCommentRow  } from "../../../backendServices/moderationManagment/api_videoComments";
import { moderateComment        } from "../../../backendServices/moderationManagment/api_comment";
import { WWW_URLS               } from "../../../backendServices/urls";
import { Segment, initials, tintOf, groupByDay, hour, plural } from "./dashboardShared";

type StatusFilter = 'all' | 'approved' | 'pending' | 'rejected';
type Preset       = 'today' | 'yesterday' | '7' | '30' | 'custom';

const STATUS_LABEL: Record<string, string> = { approved: 'PUBLIÉ', pending: 'À VALIDER', rejected: 'RETIRÉ' };
const STATUS_CLASS: Record<string, string> = { approved: styles.bOk, pending: styles.bWait, rejected: styles.bOff };
const LONG_TEXT = 220;

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n: number) => day(new Date(Date.now() - n * 86400_000));

const presetRange = (p: Exclude<Preset, 'custom'>): [string, string] =>
  p === 'today' ? [daysAgo(0), daysAgo(0)] : p === 'yesterday' ? [daysAgo(1), daysAgo(1)] : p === '7' ? [daysAgo(6), daysAgo(0)] : [daysAgo(29), daysAgo(0)];

const fullDate = (d: Date | null) =>
  d && !isNaN(d.getTime()) ? d.toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' }) : '';

const relative = (d: Date | null) => {
  if (!d || isNaN(d.getTime())) return '';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60_000));
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  if (min < 24 * 60) return `il y a ${Math.round(min / 60)} h`;
  return `à ${hour(d.toISOString())}`;
};

/** Surligne la recherche dans un texte. */
const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const q = query.trim();
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return <>{text.slice(0, i)}<mark>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

const Thumb: React.FC<{ c: RecentVideoCommentRow; onPlay: () => void }> = ({ c, onPlay }) => {
  const [ failed, setFailed ] = useState(false);
  const src = c.videoPublicRef ? `${WWW_URLS.MediaServer}${c.videoPublicRef}/images/thumbnail.webp` : '';
  return (
    <button type="button" className={styles.thumb} style={{ background: tintOf(c.videoId ?? 0) }} disabled={!c.videoPublicRef}
            onClick={onPlay} title={c.videoPublicRef ? 'Voir la vidéo' : 'Vidéo indisponible'} aria-label={`Voir la vidéo ${c.videoTitle ?? ''}`}>
      {src && !failed
        ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} />
        : <span className={styles.thumbName}>{c.videoTitle ?? 'Vidéo'}</span>}
      {c.videoPublicRef && <span className={styles.play} aria-hidden="true">▶</span>}
    </button>
  );
};

interface Props {
  adminId : number | undefined;
  /** Ouvre le lecteur du Dashboard sur la vidéo commentée. */
  onPlay  : (title: string, publicRef: string) => void;
}

/**
 * Liste des commentaires qui remplace, dans l'onglet Vidéos, les fiches et leurs filtres.
 * Recherche (vidéo, commentateur, texte), raccourcis de période + dates, filtre par statut,
 * regroupement par jour. « Retirer » rejette le commentaire (statut « rejected ») : il disparaît
 * du site mais reste en base, et peut être rétabli.
 */
const CommentsPanel: React.FC<Props> = ({ adminId, onPlay }) => {
  const [ range , setRange  ] = useState<[string, string]>(() => presetRange('7'));
  const [ query , setQuery  ] = useState('');
  const [ status, setStatus ] = useState<StatusFilter>('all');
  const [ rows  , setRows   ] = useState<RecentVideoCommentRow[]>([]);
  const [ error , setError  ] = useState('');
  const [ loading, setLoading ] = useState(true);
  const [ busy  , setBusy   ] = useState<number | null>(null);
  const [ open  , setOpen   ] = useState<Set<number>>(new Set());
  const [ undo  , setUndo   ] = useState<RecentVideoCommentRow | null>(null);
  const undoTimer = useRef<number>();
  const [ from, to ] = range;

  const preset: Preset = (['today', 'yesterday', '7', '30'] as const).find(p => presetRange(p)[0] === from && presetRange(p)[1] === to) ?? 'custom';

  useEffect(() => {
    if (!adminId) return;
    let cancelled = false;
    setLoading(true);
    getRecentVideoComments(adminId, from, to).then(r => {
      if (cancelled) return;
      setRows(r.comments); setError(r.error); setLoading(false);
    });
    return () => { cancelled = true; };
  }, [adminId, from, to]);

  useEffect(() => () => window.clearTimeout(undoTimer.current), []);

  const counts = useMemo(() => {
    const c = { all: rows.length, approved: 0, pending: 0, rejected: 0 };
    rows.forEach(r => { if (r.status in c) c[r.status as keyof typeof c]++; });
    return c;
  }, [rows]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(c =>
      (status === 'all' || c.status === status) &&
      (!q || `${c.author} ${c.videoTitle ?? ''} ${c.creatorName ?? ''} ${c.text}`.toLowerCase().includes(q)));
  }, [rows, query, status]);

  const groups = useMemo(() => groupByDay(shown, c => c.creationDate ? c.creationDate.toISOString() : null), [shown]);

  const change = async (c: RecentVideoCommentRow, action: 'approve' | 'reject') => {
    if (!adminId) return;
    setBusy(c.id); setError('');
    const res = await moderateComment(adminId, c.id, action);
    setBusy(null);
    if (res.code !== 1) { setError(res.comment || 'Action impossible.'); return; }
    const next = res.payload.status || (action === 'approve' ? 'approved' : 'rejected');
    setRows(list => list.map(x => x.id === c.id ? { ...x, status: next } : x));
    window.clearTimeout(undoTimer.current);
    if (action === 'reject') {
      setUndo(c);
      undoTimer.current = window.setTimeout(() => setUndo(null), 8000);
    } else setUndo(null);
  };

  const filtered = query.trim() !== '' || status !== 'all';
  const reset    = () => { setQuery(''); setStatus('all'); };

  return (
    <div className={styles.panel}>
      <div className={styles.filters}>
        <div className={styles.row}>
          <label className={`${styles.field} ${styles.grow}`}>Rechercher
            <span className={styles.searchBox}>
              <svg className={styles.searchIcon} width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="7" cy="7" r="5"/><path d="M11 11l4 4"/></svg>
              <input className={styles.input} type="text" value={query} placeholder="Vidéo, commentateur, créateur ou texte…" onChange={e => setQuery(e.target.value)} />
              {query && <button type="button" className={styles.clear} onClick={() => setQuery('')} aria-label="Effacer la recherche">✕</button>}
            </span>
          </label>
          <div className={styles.field}>Période
            <Segment label="Période" value={preset} onChange={p => p !== 'custom' && setRange(presetRange(p))}
                     choices={[ ['today', "Aujourd'hui"], ['yesterday', 'Hier'], ['7', '7 jours'], ['30', '30 jours'], ['custom', 'Personnalisée'] ] as const} />
          </div>
          <div className={styles.dates}>
            <label className={styles.field}>Du
              <input className={styles.input} type="date" value={from} max={to || undefined} onChange={e => e.target.value && setRange([e.target.value, to])} />
            </label>
            <span className={styles.dash} aria-hidden="true">→</span>
            <label className={styles.field}>Au
              <input className={styles.input} type="date" value={to} min={from || undefined} onChange={e => e.target.value && setRange([from, e.target.value])} />
            </label>
          </div>
        </div>
        <div className={styles.row}>
          <Segment label="Statut" value={status} onChange={setStatus}
                   choices={[ ['all', `Tous ${counts.all}`], ['approved', `Publiés ${counts.approved}`],
                              ...(counts.pending ? [['pending', `À valider ${counts.pending}`] as const] : []),
                              ['rejected', `Retirés ${counts.rejected}`] ] as const} />
          <span className={styles.summary} aria-live="polite">
            {loading ? 'Chargement…' : <><strong>{plural(shown.length, 'commentaire')}</strong>{filtered && rows.length !== shown.length ? ` sur ${rows.length}` : ''}</>}
          </span>
        </div>
      </div>

      {error && <div className={styles.error} role="alert">{error}</div>}

      {loading && rows.length === 0 && [0, 1, 2].map(i => <div key={i} className={styles.skeleton} />)}

      {!loading && !error && shown.length === 0 && (
        <div className={styles.empty}>
          <strong>{filtered ? 'Aucun commentaire ne correspond' : 'Aucun commentaire sur cette période'}</strong>
          <span>{filtered ? 'Essayez un autre nom ou retirez le filtre de statut.' : 'Élargissez la période pour remonter plus loin.'}</span>
          {filtered
            ? <button type="button" className={styles.btn} onClick={reset}>Réinitialiser les filtres</button>
            : preset !== '30' && <button type="button" className={styles.btn} onClick={() => setRange(presetRange('30'))}>Voir les 30 derniers jours</button>}
        </div>
      )}

      {groups.map(g => (
        <section key={g.title} className={styles.day}>
          <h3 className={styles.dayTitle}>{g.title} <span className={styles.dayCount}>{plural(g.items.length, 'commentaire')}</span></h3>
          {g.items.map(c => {
            const long     = c.text.length > LONG_TEXT || c.text.split('\n').length > 3;
            const expanded = open.has(c.id);
            const removed  = c.status === 'rejected';
            return (
              <article key={c.id} className={`${styles.item} ${removed ? styles.removed : ''}`}>
                <Thumb c={c} onPlay={() => c.videoPublicRef && onPlay(c.videoTitle ?? '', c.videoPublicRef)} />

                <div className={styles.body}>
                  <div className={styles.head}>
                    <span className={styles.avatar} style={{ background: tintOf(c.ownerId ?? 0) }} aria-hidden="true">{initials(c.author)}</span>
                    <span className={styles.author}><Highlight text={c.author} query={query} /></span>
                    <span className={styles.when} title={fullDate(c.creationDate)}>{relative(c.creationDate)}</span>
                    <span className={`${styles.badge} ${STATUS_CLASS[c.status] ?? ''}`}>{STATUS_LABEL[c.status] ?? c.status.toUpperCase()}</span>
                  </div>

                  <p className={`${styles.text} ${long && !expanded ? styles.clamped : ''}`}><Highlight text={c.text} query={query} /></p>
                  {long && (
                    <button type="button" className={styles.more}
                            onClick={() => setOpen(s => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })}>
                      {expanded ? 'Réduire' : 'Lire la suite'}
                    </button>
                  )}

                  <div className={styles.facts}>
                    <span className={styles.chip} title={c.videoTitle ?? ''}><em>Vidéo n° {c.videoId ?? '—'}</em><b><Highlight text={c.videoTitle ?? '—'} query={query} /></b></span>
                    <span className={styles.chip} title={c.creatorName ?? ''}><em>Créateur n° {c.creatorId ?? '—'}</em><b><Highlight text={c.creatorName ?? '—'} query={query} /></b></span>
                    {c.ip && <span className={`${styles.chip} ${styles.mono}`} title="Adresse IP au moment de l'envoi"><em>IP</em><b>{c.ip}</b></span>}
                  </div>
                </div>

                <div className={styles.actions}>
                  <button type="button" className={styles.btn} disabled={!c.videoPublicRef}
                          onClick={() => c.videoPublicRef && onPlay(c.videoTitle ?? '', c.videoPublicRef)}>▶ Voir la vidéo</button>
                  {removed
                    ? <button type="button" className={`${styles.btn} ${styles.restore}`} disabled={busy === c.id}
                              title="Remettre le commentaire en ligne" onClick={() => change(c, 'approve')}>↺ Rétablir</button>
                    : <button type="button" className={`${styles.btn} ${styles.danger}`} disabled={busy === c.id}
                              title="Le commentaire n’est plus affiché mais reste en base" onClick={() => change(c, 'reject')}>✕ Retirer</button>}
                </div>
              </article>
            );
          })}
        </section>
      ))}

      {undo && (
        <div className={styles.toast} role="status">
          <span>Commentaire de <strong>{undo.author}</strong> retiré.</span>
          <button type="button" className={`${styles.btn} ${styles.restore}`} onClick={() => change(undo, 'approve')}>Annuler</button>
        </div>
      )}
    </div>
  );
};

export default CommentsPanel;
